// MaturaArithmeticSumClosedTask.js
// Zadanie maturalne – zamknięte (ciąg arytmetyczny):
// dane są a_1 oraz a_n, szukamy sumy S_n pierwszych n wyrazów (ABCD)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

function shuffle(array) {
  const a = [...array];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function intToLatex(n) {
  if (n < 0) return `(${n})`;
  return String(n);
}

/**
 * Wariant „oryginał” – matura podstawowa, czerwiec 2025, zadanie 15.2:
 *   ciąg arytmetyczny, a_1 = 52, a_25 = 2;  S_25 = (52+2)/2 · 25 = 675  (A).
 */
function createJune2025OriginalTask() {
  const options = [
    { label: "675", value: 675, key: "A" },
    { label: "700", value: 700, key: "B" },
    { label: "1300", value: 1300, key: "C" },
    { label: "1325", value: 1325, key: "D" },
  ];

  return {
    type: "MaturaArithmeticSumClosedTask",
    data: {
      n: 25,
      a1: 52,
      an: 2,
      S: 675,
      options,
      correctIndex: 0,
      maxPoints: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: "15.2",
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  // liczba dodawanych początkowych wyrazów
  const n = randInt(20, 45);

  // pierwszy wyraz
  const a1 = randInt(15, 35);

  // różnica ciągu (niewykorzystywana w treści, tylko do zbudowania a_n)
  let r;
  do {
    r = randInt(-5, 5);
  } while (r === 0);

  const an = a1 + (n - 1) * r;

  // poprawna suma S_n = (a_1 + a_n) * n / 2
  const S = ((a1 + an) * n) / 2;

  // S powinno wyjść całkowite, ale na wszelki wypadek zaokrąglamy
  const S_int = Math.round(S);

  // dystraktory w stylu arkusza:
  // poprawne:  S
  // błędne:    S + n, 2S - 2n, 2S - n
  // (dla przykładu z CKE: 675, 700, 1300, 1325)
  const wrong1 = S_int + n;
  const wrong2 = 2 * S_int - 2 * n;
  const wrong3 = 2 * S_int - n;

  let values = [S_int, wrong1, wrong2, wrong3];

  // zabezpieczenie przed duplikatami
  values = Array.from(new Set(values));
  while (values.length < 4) {
    const candidate = S_int + randInt(-5, 5) * n;
    if (!values.includes(candidate)) values.push(candidate);
  }
  values = values.slice(0, 4);

  const shuffled = shuffle(values);

  const options = shuffled.map((v, idx) => ({
    label: String(v),
    value: v,
    key: LETTERS[idx],
  }));

  const correctIndex = shuffled.indexOf(S_int);

  return {
    type: "MaturaArithmeticSumClosedTask",
    data: {
      n,
      a1,
      an,
      S: S_int,
      options,
      correctIndex,
      maxPoints: 1,
    },
  };
}

export function renderTask({ data }) {
  const { n, a1, an, options } = data;

  const a1Latex = intToLatex(a1);
  const anLatex = intToLatex(an);

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        W ciągu arytmetycznym <InlineMath math={'(a_n)'} />, określonym dla każdej
        liczby naturalnej <InlineMath math={'n \\ge 1'} />, dane są wyrazy{" "}
        <InlineMath math={`a_1 = ${a1Latex}`} /> oraz{" "}
        <InlineMath math={`a_{${n}} = ${anLatex}`} />.
      </p>
      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>
      <p>
        Suma <InlineMath math={`S_{${n}}`} /> <InlineMath math={String(n)} /> początkowych kolejnych wyrazów
        ciągu <InlineMath math={'(a_n)'} /> jest równa:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.key} style={{ marginBottom: 8 }}>
            <InlineMath math={`${opt.key}.\\; ${opt.label}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.key,
    textNode: opt.key,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.key,
      latex: opt.label,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    userAnswer.latex == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;
  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options[correctIndex];

  return <InlineMath math={`${correct.key}.\\; ${correct.label}`} />;
}

export function renderExplanation({ data }) {
  const { n, a1, an, S } = data;

  const a1Latex = intToLatex(a1);
  const anLatex = intToLatex(an);

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Korzystamy ze wzoru na sumę{" "}
        <InlineMath math={"n"} /> początkowych wyrazów ciągu arytmetycznego:
      </p>

      {/* FIX: poprawne backslashe dla KaTeX */}
      <BlockMath math={"S_n = \\dfrac{a_1 + a_n}{2} \\cdot n"} />

      <p>
        <strong>Krok 2.</strong> Podstawiamy dane z treści zadania:
      </p>
      <BlockMath math={`S_{${n}} = \\dfrac{a_1 + a_{${n}}}{2}\\cdot ${n}`} />
      <BlockMath math={`S_{${n}} = \\dfrac{${a1Latex} + ${anLatex}}{2}\\cdot ${n}`} />

      <p>
        <strong>Krok 3.</strong> Obliczamy wartość sumy:
      </p>
      <BlockMath math={`S_{${n}} = ${S}`} />

      <p>
        Zatem suma <InlineMath math={`S_{${n}}`} /> jest równa{" "}
        <InlineMath math={String(S)} />
        <InlineMath math={"\\text{.}"} />
      </p>
    </div>
  );
}



export default {
  generateTask,
  renderTask,
  renderInput,
  renderUserAnswer,
  checkAnswer,
  renderAnswer,
  renderExplanation,
};
